import { useState, useEffect } from 'react';
import './ProjectForm.css';

interface ProjectFormData {
  name: string;
  slug?: string;
  description?: string;
}

interface ProjectFormProps {
  initialData?: {
    name: string;
    description: string;
  };
  onSubmit: (data: ProjectFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
  isEditing?: boolean;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug);
}

export function ProjectForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  error,
  isEditing = false,
}: ProjectFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState(initialData?.description || '');
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description || '');
    }
  }, [initialData]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);

    // Auto-generate slug if not manually edited (only for new projects)
    if (!isEditing && !slugEdited) {
      setSlug(generateSlug(newName));
      setSlugError(null);
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value.toLowerCase();
    setSlug(newSlug);
    setSlugEdited(true);

    // Validate slug format
    if (newSlug && !isValidSlug(newSlug)) {
      setSlugError('Slug can only contain lowercase letters, numbers, and hyphens');
    } else {
      setSlugError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;
    if (slugError) return;

    const data: ProjectFormData = {
      name: name.trim(),
      description: description.trim() || undefined,
    };

    // Only include slug for new projects
    if (!isEditing && slug.trim()) {
      data.slug = slug.trim();
    }

    await onSubmit(data);
  };

  const canSubmit = name.trim() && !slugError && !isLoading;

  return (
    <form className="project-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="name">Project Name</label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={handleNameChange}
          placeholder="My App"
          autoFocus
          required
        />
      </div>

      {!isEditing && (
        <div className="form-group">
          <label htmlFor="slug">
            URL Slug
            <span className="label-hint">
              Used in URLs and API endpoints. Cannot be changed after creation.
            </span>
          </label>
          <div className="slug-input">
            <span className="slug-prefix">/</span>
            <input
              id="slug"
              type="text"
              value={slug}
              onChange={handleSlugChange}
              placeholder="my-app"
            />
          </div>
          {slugError && <p className="form-error">{slugError}</p>}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="description">
          Description
          <span className="label-hint">(optional)</span>
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="A brief description of this project"
          rows={3}
        />
      </div>

      {error && <p className="form-error">{error}</p>}

      <div className="form-actions">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
          {isLoading
            ? isEditing
              ? 'Saving...'
              : 'Creating...'
            : isEditing
              ? 'Save Changes'
              : 'Create Project'}
        </button>
      </div>
    </form>
  );
}
